/**
 * Main entry point for Ragsmith Web UI
 */

import { state, resetNarratorState, loadSessionState, saveSessionStateWithModel, cycleWebSearchDepth, togglePlayback, toggleImageSearch, toggleTool, resetDisabledTools, togglePlanning, toggleSubordinateTool, toggleDictationMode, toggleDictationExtractFacts, API_BASE, setContainerMode, isContainerMode, isCapsuleMode } from './state.js';
import { renderCompactionDivider, initContextMeter } from './context-meter.js';
import { escapeHtml } from './html-escape.js';
import './message-editor.js';
import './message-export.js';
import {
    initElements,
    elements,
    updateStatus,
    updateModeIndicators,
    populateAgents,
    populateModels,
    addSystemMessage,
    clearChat,
    addMessage,
    addMetaToMessage,
    showCollectionHeader,
    hideCollectionHeader,
    initDeleteModal,
    initRedoModal,
    hasThinkingContent,
    separateThinkingContent,
    addThinkingToMessage,
    addAnalyticsToMessage,
    renderFactIngestChip,
    renderRecordingIngestCard,
    applyPlaybackVisibility
} from './ui.js';
import * as api from './api.js';
import { commands } from './commands.js';
import { sendMessage, stopAll, loadAgentHandler, loadCollectionHandler } from './chat.js';
import {
    initMenu,
    populateMenuAgents,
    populateMenuCollections,
    populateMenuModels,
    healUnservableModel,
    honestProvider,
    providerForValue,
    updateMenuLoadButton,
    updateHeaderStatus,
    updateAgentDisplay,
    updateCollectionDisplay,
    getModelDisplayName,
    findTierByModelName,
    applyContainerModeRestrictions,
    applyWorkspaceModeRestrictions
} from './menu.js';
import {
    initConversations,
    loadConversation,
    isErrorMessageContent,
    restoreRetryAffordances,
} from './conversations.js';
import { initModelManager, updateCurrentModelDisplay, setToolingRequired } from './model-manager.js';
import { initInput } from './input.js';
import { initCamera, openCamera, openVideoCamera } from './camera.js';
import { initAttachmentViewer } from './attachment-viewer.js';
import { initAuth, checkAuthRequired } from './auth.js';
import { initConfirmDialog } from './confirm-dialog.js';
import { initVoice, preloadSoundEffects } from './voice.js';
import { initTtsPlayer } from './tts-player.js';
import { initVoiceManager } from './voice-manager.js';
import { initAnalytics } from './analytics-manager.js';
import { getHeaders } from './api.js';
import { initCapsuleManager, openCapsuleManager } from './capsule-manager.js';
import { initAudioRecorder, startRecording, openAudioManager } from './audio-recorder.js';

/**
 * Hide the connecting overlay with fade animation.
 * Preloads voice models (VAD + STT + TTS) if available and not yet in memory.
 */
async function hideConnectingOverlay() {
    const overlay = document.getElementById('connecting-overlay');
    if (!overlay) return;

    // Hide the overlay NOW — the chat model is ready, so the user can start
    // typing. Voice + sound effects preload in the BACKGROUND; they must never
    // hold up the connect. (Previously this blocked on loading Whisper+Kokoro
    // into the front box — brutal on a CPU box whose voice is delegated.)
    overlay.classList.add('hidden');
    preloadVoiceInBackground();
}

// Full-screen splash for a container whose ASSIGNED compute workers are all
// offline (exclusive assignment). Auto-polls until a worker comes back, then
// reloads into the normal boot. Reuses the busy-overlay styling.
function showComputeOfflineSplash(containerToken) {
    document.getElementById('connecting-overlay')?.classList.add('hidden');
    const overlay = document.createElement('div');
    overlay.className = 'busy-overlay';
    overlay.innerHTML = `
        <div class="busy-content">
            <div class="busy-spinner"></div>
            <div class="busy-title">Workspace compute is offline</div>
            <div class="busy-text">
                The machine that powers this workspace isn't running right now.
                We'll connect you automatically the moment it comes back —
                or check again with the button below.
            </div>
            <button class="busy-restart" id="compute-offline-retry">Check again</button>
        </div>`;
    document.body.appendChild(overlay);

    let checking = false;
    const check = async () => {
        if (checking) return;
        checking = true;
        try {
            const v = await api.validateContainerToken(containerToken);
            // Back online (or assignment lifted) — boot normally.
            if (v.valid && v.workers_available !== false) location.reload();
            // Token revoked/expired while waiting: fall through to normal
            // boot too, which renders the proper invalid-token screen.
            if (!v.valid) location.reload();
        } catch { /* transient — keep waiting */ }
        checking = false;
    };
    document.getElementById('compute-offline-retry').addEventListener('click', check);
    setInterval(check, 15000);
}

// Fire-and-forget voice + SFX warmup. When voice is delegated to a GPU worker,
// /voice/status reports models_loaded=true and we skip the local load entirely.
let _voicePreloadStarted = false;
async function preloadVoiceInBackground() {
    if (_voicePreloadStarted) return;
    _voicePreloadStarted = true;
    try {
        const res = await fetch(`${API_BASE}/voice/status`, { headers: getHeaders(false) });
        if (res.ok) {
            const data = await res.json();
            // Only load the heavy pipelines locally when NOT delegated.
            if (data.available && data.models_ready && !data.models_loaded) {
                const dl = await fetch(`${API_BASE}/voice/models/download?full=true`, { headers: getHeaders(false) });
                const reader = dl.body.getReader();
                while (!(await reader.read()).done) {}
            }
        }
    } catch (e) { /* best-effort */ }
    try { await preloadSoundEffects(); } catch (e) { /* non-blocking */ }
}

/**
 * Show connection error in the overlay with auto-retry countdown
 */
function showConnectionError() {
    const overlay = document.getElementById('connecting-overlay');
    if (overlay) {
        const text = overlay.querySelector('.connecting-text');
        const spinner = overlay.querySelector('.connecting-spinner');
        if (spinner) spinner.style.borderTopColor = '#f87171';

        let seconds = 5;
        if (text) text.textContent = `Connection failed. Retrying in ${seconds}s...`;

        const countdown = setInterval(() => {
            seconds--;
            if (seconds > 0) {
                if (text) text.textContent = `Connection failed. Retrying in ${seconds}s...`;
            } else {
                clearInterval(countdown);
                if (text) text.textContent = 'Reconnecting...';
                if (spinner) spinner.style.borderTopColor = '';
                init();
            }
        }, 1000);
    }
}

/**
 * Show a full-screen error message (for expired/invalid containers and capsules).
 * Hides all app UI and keeps only the error overlay visible.
 */
function showLinkError(message) {
    // Hide all app UI
    document.querySelectorAll('header, #menu-panel, .input-row, #chat-history, .status-indicators, .mode-indicators, .plus-menu-container')
        .forEach(el => el.style.display = 'none');
    document.body.style.overflow = 'hidden';

    // Repurpose the connecting overlay as the error display
    const overlay = document.getElementById('connecting-overlay');
    if (overlay) {
        const text = overlay.querySelector('.connecting-text');
        const spinner = overlay.querySelector('.connecting-spinner');
        if (text) text.textContent = message;
        if (spinner) spinner.style.display = 'none';
        overlay.style.opacity = '1';
        overlay.style.display = 'flex';
    }
}

/**
 * Initialize the application
 */
async function init() {
    // Initialize DOM element references
    initElements();

    // Initialize auth module (injects login modal)
    initAuth();

    // Check for capsule token in URL or sessionStorage (capsule = shared conversation link)
    const urlParams = new URLSearchParams(window.location.search);
    let capsuleToken = urlParams.get('c');
    if (!capsuleToken) {
        capsuleToken = sessionStorage.getItem('ragsmith_capsule');
    }
    if (capsuleToken) {
        try {
            const { initCapsuleMode } = await import('./capsule.js');
            const ok = await initCapsuleMode(capsuleToken, urlParams.has('c'));
            if (ok) {
                // Initialize modals and TTS for capsule mode
                initDeleteModal();
                initConfirmDialog();
                initRedoModal();
                initTtsPlayer();
                return;
            }
            // If capsule invalid and it came from URL, show error and stop
            if (urlParams.has('c')) {
                showLinkError('This shared link is invalid or has expired.');
                return;
            }
        } catch (e) {
            console.error('[Capsule] Failed to initialize capsule mode:', e);
            if (urlParams.has('c')) {
                showLinkError('Failed to load shared link.');
                return;
            }
        }
        // If restored from sessionStorage and invalid, silently fall through
    }

    // Check for container token in URL or sessionStorage (container tokens bypass auth)
    let containerToken = urlParams.get('u');

    // If no URL token, check sessionStorage for persisted container session
    if (!containerToken) {
        const savedContainer = sessionStorage.getItem('ragsmith_container');
        if (savedContainer) {
            try {
                const parsed = JSON.parse(savedContainer);
                containerToken = parsed.token;
            } catch (e) {
                sessionStorage.removeItem('ragsmith_container');
            }
        }
    }

    // If still no token, check localStorage for across-session persistence
    if (!containerToken) {
        containerToken = localStorage.getItem('ragsmith_container_token');
    }

    if (containerToken) {
        // Validate container token (checks expiration, etc.)
        const validation = await api.validateContainerToken(containerToken);

        if (validation.valid) {
            // Exclusive compute assignment: if every worker assigned to this
            // container is offline, show a graceful splash (with auto-retry)
            // instead of a workspace that fails on first message.
            if (validation.workers_available === false) {
                showComputeOfflineSplash(containerToken);
                return;
            }
            // Set container mode state
            setContainerMode(containerToken, {
                agent: validation.agent,
                collection: validation.collection,
                model: validation.model,
                model_type: validation.model_type,
                voice: validation.voice,
                mode: validation.mode || 'demo',
                allowed_models: validation.allowed_models,
                allow_leave: validation.allow_leave !== false,
                custom_api: validation.custom_api || false,
                allow_automation: validation.allow_automation === true,
                has_api_keys: validation.has_api_keys || false,
                default_input_mode: validation.default_input_mode || null
            });

            // If custom_api is required but no keys are set, show setup form BEFORE anything else loads
            // The connecting overlay stays behind (lower z-index) — after form submit, normal init resumes
            if (validation.custom_api && !validation.has_api_keys) {
                const { showApiKeySetup } = await import('./api-key-manager.js');
                await showApiKeySetup(containerToken);
                // After setup, update has_api_keys in state
                state.containerConfig.has_api_keys = true;
            }

            // Persist to sessionStorage for page refreshes
            sessionStorage.setItem('ragsmith_container', JSON.stringify({
                token: containerToken,
                agent: validation.agent,
                collection: validation.collection,
                model: validation.model,
                model_type: validation.model_type,
                voice: validation.voice,
                mode: validation.mode || 'demo',
                allowed_models: validation.allowed_models,
                allow_leave: validation.allow_leave !== false,
                custom_api: validation.custom_api || false,
                allow_automation: validation.allow_automation === true,
                has_api_keys: validation.has_api_keys || false
            }));

            // Persist token to localStorage for across-session persistence
            localStorage.setItem('ragsmith_container_token', containerToken);

            // Apply container voice (always override localStorage with configured voice)
            if (validation.voice) {
                localStorage.setItem('ragsmith_tts_voice', validation.voice);
            }

            // Remove token from URL for cleaner appearance
            if (urlParams.get('u')) {
                const cleanUrl = window.location.pathname;
                window.history.replaceState({}, '', cleanUrl);
            }
        } else {
            // Invalid/expired token - clear all storage
            sessionStorage.removeItem('ragsmith_container');
            localStorage.removeItem('ragsmith_container_token');

            // Only show error if token came from URL (explicit user action)
            // If auto-restored from storage, silently fall through to normal init
            if (urlParams.get('u')) {
                showLinkError('This container is expired or invalid.');
                return;
            }
        }
    }

    // Check if authentication is required (for remote access)
    // Skip auth check if in container mode (container tokens have already been validated)
    if (!isContainerMode()) {
        const canProceed = await checkAuthRequired();
        if (!canProceed) {
            // Wait for auth-success event, then re-run init
            window.addEventListener('auth-success', () => init(), { once: true });
            return;
        }
    }

    // Initialize hamburger menu
    initMenu();

    // Initialize conversations module
    initConversations();

    // Initialize Model Manager modal
    initModelManager();

    // Initialize Voice Manager modal (TTS voice selector)
    initVoiceManager();

    // Initialize Analytics Dashboard modal
    initAnalytics();

    // Initialize Attachment Viewer modal
    initAttachmentViewer();

    // Initialize Delete Confirmation modal
    initDeleteModal();

    // Initialize Confirm Dialog (replaces browser confirm())
    initConfirmDialog();

    // Initialize Redo/Edit Prompt modal
    initRedoModal();

    // Initialize Capsule Manager modal (Share Links)
    initCapsuleManager();

    // Check for saved session - if exists, try quick restore first
    // Skip restore if the saved session came from a different context
    // (e.g., admin session loaded in container, or different container)
    let savedSession = loadSessionState();
    if (savedSession && isContainerMode()) {
        // Only restore if the saved session belongs to this container
        if (savedSession.containerToken !== state.containerToken) {
            savedSession = null;
        } else if (state.containerConfig?.model) {
            // The container's model is resolved server-side at validate time:
            // it follows the pool and falls to a cloud lane when the pool is
            // empty. A model saved in this browser predates all of that, so
            // restoring it shows — and warms — a model nothing is serving.
            savedSession = { ...savedSession, modelTier: state.containerConfig.model };
        }
    }

    if (savedSession && savedSession.loadedAgent) {
        // Try quick restore path - health check before loading agents/models
        try {
            const health = await api.checkHealth(savedSession.loadedAgent);

            if (health.agent_loaded) {
                // Fast path: agent is cached, restore immediately

                state.loadedAgent = savedSession.loadedAgent;
                state.currentAgent = savedSession.loadedAgent;
                state.loadedCollection = null;
                state.loadedCollectionTitle = null;
                state.loadedCollectionDescription = null;

                // Restore model state (don't load, just set state - model warms on first message)
                if (savedSession.modelTier) {
                    state.loadedModel = savedSession.modelTier;
                    state.loadedProvider = savedSession.loadedProvider || null;
                }

                // Restore narrator mode from saved state
                if (savedSession.narratorMode && savedSession.narratorState) {
                    state.narratorMode = true;
                    state.narratorPaused = savedSession.narratorPaused || false;
                    state.narratorState = savedSession.narratorState;
                }

                // Restore conversation
                if (savedSession.sessionId) {
                    await restoreConversation(savedSession.sessionId, savedSession.loadedAgent);
                }

                // Update UI
                updateModeIndicators(savedSession.loadedAgent, null, state.narratorMode, state.narratorState?.chunk_id || 1);
                updateHeaderStatus();
                hideConnectingOverlay();
                hideCollectionHeader();
                addSystemMessage(`Session restored. ${savedSession.loadedAgent} ready.`);

                // Re-apply container restrictions after session restore (ensures friendly model name)
                if (isContainerMode()) {
                    applyContainerModeRestrictions();
                }

                // Set up event listeners early
                setupEventListeners();

                // Fetch agent settings in background to restore tool toggles
                fetch(`/agents/${encodeURIComponent(savedSession.loadedAgent)}/settings`, {
                    headers: api.getHeaders(false)
                }).then(r => r.ok ? r.json() : null).then(async (data) => {
                    if (data?.settings) {
                        state.agentSettings = data.settings;
                        state.agentKbCount = data.kb_count || 0;
                        window.populateToolToggles?.(data.settings.tools || [], data.settings);
                        window.updateNarratorMenuSection?.();
                        // Update voice button icon for narrator mode
                        const { updateVoiceBtnIcon } = await import('./voice.js');
                        const effectiveNarrator = state.narratorMode && !state.narratorPaused;
                        updateVoiceBtnIcon(effectiveNarrator, !!data.settings.narrator_verbatim);
                    }
                }).catch(() => {});

                // Load agents/models/status in background (don't block UI)
                loadDropdownsInBackground(savedSession);
                return;
            }
        } catch (e) {
            // Health check failed, fall back to full init
        }
    }

    // Full initialization path (no saved session, or agent not cached)
    await fullInit(savedSession);
}

/**
 * Load dropdown data in background after quick restore
 */
async function loadDropdownsInBackground(savedSession) {
    // Load status
    api.checkStatus().then(data => {
        updateStatus(`Online | ${data.kb_count} KBs | ${data.agent_count} agents`, true);
    }).catch(() => {});

    // Load agents
    api.fetchAgents().then(data => {
        populateAgents(data.agents);
        populateMenuAgents(data.agents);
        // Restore selection after populating
        if (savedSession.loadedAgent) {
            elements.agentSelect.value = savedSession.loadedAgent;
            const menuAgentSelect = document.getElementById('menu-agent-select');
            if (menuAgentSelect) menuAgentSelect.value = savedSession.loadedAgent;
            // Update display button
            const displayName = savedSession.loadedAgent
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            updateAgentDisplay(displayName);
        }
        updateMenuLoadButton();
    }).catch(e => console.error('Failed to load agents:', e));

    // Load collections
    api.fetchCollections().then(data => {
        populateMenuCollections(data.collections);
        // Restore selection after populating
        if (savedSession.loadedCollection) {
            const menuCollectionSelect = document.getElementById('menu-collection-select');
            if (menuCollectionSelect) menuCollectionSelect.value = savedSession.loadedCollection;
            // Update display button - find title from data
            const collection = data.collections.find(c => c.name === savedSession.loadedCollection);
            const displayName = collection?.title || savedSession.loadedCollection
                .split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            updateCollectionDisplay(displayName);
        }
        updateMenuLoadButton();
    }).catch(e => console.error('Failed to load collections:', e));

    // Load models
    api.fetchModels().then(data => {
        populateModels(data.tiers);
        populateMenuModels(data.tiers, data);
        // Restore selection after populating
        if (savedSession.modelTier) {
            // A remembered provider can outlive the model it described: the
            // header read "OpenRouter (Qwen 3.5 4B)" for a model that runs on
            // a worker. Believe the model, which also lets the heal below see
            // it as local.
            const savedProvider = honestProvider(savedSession.modelTier, savedSession.loadedProvider);
            if (savedProvider !== (savedSession.loadedProvider || null)) {
                savedSession = { ...savedSession, loadedProvider: savedProvider };
            }
            // A remembered model whose crew has since been stood down must not
            // come back on refresh. See healUnservableModel.
            const healed = healUnservableModel(savedSession.modelTier, savedProvider || 'local');
            let healedProvider = null;
            if (healed) {
                console.warn(`[Init] ${savedSession.modelTier} is not being served; using ${healed.modelName}`);
                // The heal can land on the cloud lane, which the remembered
                // provider ("local") would then mislabel.
                healedProvider = providerForValue(healed.value)
                    || (healed.value.includes('/') ? 'openrouter' : 'local');
                savedSession = { ...savedSession, modelTier: healed.value, loadedProvider: healedProvider };
            }
            state.loadedModel = savedSession.modelTier;  // Restore model state
            state.loadedProvider = healedProvider || savedSession.loadedProvider || null;
            if (healed || savedProvider !== state.loadedProvider) {
                saveSessionStateWithModel(savedSession.modelTier);
            }
            // modelTier may be a tier alias ("large") OR a real model name
            // ("qwen3.5:9b" — anything loaded via the model manager,
            // including vLLM-worker models). Resolve to a dropdown value;
            // assigning an unknown value to a <select> silently sets
            // selectedIndex=-1 and the header chip vanished on refresh.
            const dropdownValue = findTierByModelName(savedSession.modelTier)
                || savedSession.modelTier;
            elements.modelSelect.value = dropdownValue;
            const menuModelSelect = document.getElementById('menu-model-select');
            if (menuModelSelect) menuModelSelect.value = dropdownValue;
            // Update current model display with restored model
            const provider = savedSession.loadedProvider || (
                savedSession.modelTier === 'haiku' || savedSession.modelTier === 'sonnet' ? 'anthropic' : 'local'
            );
            const selectedOption = menuModelSelect?.selectedOptions?.[0];
            const displayName = selectedOption?.dataset?.model || savedSession.modelTier;
            updateCurrentModelDisplay(savedSession.modelTier, displayName, provider, true);
        }
        updateHeaderStatus();
    }).catch(e => console.error('Failed to load models:', e));
}

/**
 * Full initialization (no saved session or agent not cached)
 */
async function fullInit(savedSession) {
    // Check API status
    try {
        const data = await api.checkStatus();
        updateStatus(`Online | ${data.kb_count} KBs | ${data.agent_count} agents`, true);
    } catch (e) {
        updateStatus('Offline - Start API server', false);
        showConnectionError();
        return;
    }

    // Load agents
    try {
        const data = await api.fetchAgents();
        populateAgents(data.agents);
        populateMenuAgents(data.agents);
    } catch (e) {
        console.error('Failed to load agents:', e);
    }

    // Load collections
    try {
        const data = await api.fetchCollections();
        populateMenuCollections(data.collections);
    } catch (e) {
        console.error('Failed to load collections:', e);
    }

    // Load models
    try {
        const data = await api.fetchModels();
        populateModels(data.tiers);
        populateMenuModels(data.tiers, data);
    } catch (e) {
        console.error('Failed to load models:', e);
    }

    if (savedSession && savedSession.loadedAgent) {
        // Restore agent selection
        elements.agentSelect.value = savedSession.loadedAgent;
        const menuAgentSelect = document.getElementById('menu-agent-select');
        if (menuAgentSelect) menuAgentSelect.value = savedSession.loadedAgent;
        // Update display button
        const agentDisplayName = savedSession.loadedAgent
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        updateAgentDisplay(agentDisplayName);

        // Restore model selection
        if (savedSession.modelTier) {
            elements.modelSelect.value = savedSession.modelTier;
            const menuModelSelect = document.getElementById('menu-model-select');
            if (menuModelSelect) menuModelSelect.value = savedSession.modelTier;
        }

        // Load the agent and restore conversation
        await restoreSession(savedSession);
    } else if (savedSession && savedSession.loadedCollection) {
        // Restore collection selection
        const menuCollectionSelect = document.getElementById('menu-collection-select');
        if (menuCollectionSelect) menuCollectionSelect.value = savedSession.loadedCollection;
        // Update display button
        const collectionDisplayName = savedSession.loadedCollectionTitle || savedSession.loadedCollection
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        updateCollectionDisplay(collectionDisplayName);

        // Restore model selection
        if (savedSession.modelTier) {
            elements.modelSelect.value = savedSession.modelTier;
            const menuModelSelect = document.getElementById('menu-model-select');
            if (menuModelSelect) menuModelSelect.value = savedSession.modelTier;
        }

        // Load the collection and restore conversation
        await restoreCollectionSession(savedSession);
    } else if (savedSession && savedSession.sessionId) {
        // Plain conversation (no agent/collection) - restore model state first, then load conversation

        // Set dropdown values for UI (don't set state.loadedModel yet —
        // loadConversation will handle VRAM loading and state)
        if (savedSession.modelTier) {
            elements.modelSelect.value = savedSession.modelTier;
            const menuModelSelect = document.getElementById('menu-model-select');
            if (menuModelSelect) menuModelSelect.value = savedSession.modelTier;
        }

        try {
            // loadConversation will fetch conversation history and may update model state from DB
            await loadConversation(savedSession.sessionId);

            // Update UI to reflect restored state
            updateMenuLoadButton();
            updateHeaderStatus();
            hideConnectingOverlay();
            addSystemMessage('Session restored.');

            // Re-apply container restrictions after session restore (ensures friendly model name)
            if (isContainerMode()) {
                applyContainerModeRestrictions();
            }
        } catch (e) {
            console.error('[Init] Failed to restore plain session:', e);
            hideConnectingOverlay();
            addSystemMessage('Failed to restore session. Starting fresh.');
        }
    } else {
        // No saved session - check for container mode, CLI defaults, or load default model
        let defaults = { agent: null, collection: null, model: null };

        // Container mode config overrides CLI defaults
        if (isContainerMode() && state.containerConfig) {
            defaults = {
                agent: state.containerConfig.agent || null,
                collection: state.containerConfig.collection || null,
                model: state.containerConfig.model || null,
                model_type: state.containerConfig.model_type || null
            };
        } else if (savedSession && savedSession.modelTier) {
            // User previously selected a model but has no agent/collection/conversation
            // Honor their model choice instead of loading CLI defaults
            defaults = { agent: null, collection: null, model: savedSession.modelTier };
        } else {
            try {
                defaults = await api.fetchDefaults();
            } catch (e) {
                // No CLI defaults available
            }
        }

        if (defaults.agent) {
            // CLI specified a default agent - load it
            const defaultModel = defaults.model || 'small';

            // Hide connecting overlay - the agent loader has its own modal
            hideConnectingOverlay();

            // Ensure model option exists in dropdowns (cloud/Groq models may not be in tiers)
            const defaultProvider = defaults.model_type || 'local';
            const menuModelSelect = document.getElementById('menu-model-select');
            if (menuModelSelect && !Array.from(menuModelSelect.options).some(o => o.value === defaultModel)) {
                const opt = document.createElement('option');
                opt.value = defaultModel;
                opt.textContent = getModelDisplayName(defaultModel) || defaultModel;
                opt.dataset.provider = defaultProvider;
                menuModelSelect.appendChild(opt);
            }
            if (elements.modelSelect && !Array.from(elements.modelSelect.options).some(o => o.value === defaultModel)) {
                const opt = document.createElement('option');
                opt.value = defaultModel;
                opt.textContent = getModelDisplayName(defaultModel) || defaultModel;
                opt.dataset.provider = defaultProvider;
                elements.modelSelect.appendChild(opt);
            }

            // Set model select and state so loadAgentHandler picks it up
            elements.modelSelect.value = defaultModel;
            if (menuModelSelect) menuModelSelect.value = defaultModel;
            state.loadedModel = defaultModel;
            state.loadedProvider = defaultProvider;

            // Set agent select
            elements.agentSelect.value = defaults.agent;
            const menuAgentSelect = document.getElementById('menu-agent-select');
            if (menuAgentSelect) menuAgentSelect.value = defaults.agent;

            // Load the agent using the standard handler (shows its own loading modal)
            try {
                await loadAgentHandler();
                // Update display buttons to show loaded agent and model
                const agentDisplayName = defaults.agent
                    .split('-')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
                updateAgentDisplay(agentDisplayName);
                const modelDisplayName = getModelDisplayName(defaultModel) || defaultModel;
                updateCurrentModelDisplay(defaultModel, modelDisplayName, defaultProvider, true);
            } catch (e) {
                console.error('[Init] Failed to load CLI default agent:', e);
                addSystemMessage(`Failed to load default agent: ${defaults.agent}`);
            }
        } else if (defaults.collection) {
            // CLI specified a default collection - load it
            const defaultModel = defaults.model || 'small';

            // Hide connecting overlay - the collection loader has its own modal
            hideConnectingOverlay();

            // Ensure model option exists in dropdowns (cloud/Groq models may not be in tiers)
            const defaultProvider = defaults.model_type || 'local';
            const menuModelSelect = document.getElementById('menu-model-select');
            if (menuModelSelect && !Array.from(menuModelSelect.options).some(o => o.value === defaultModel)) {
                const opt = document.createElement('option');
                opt.value = defaultModel;
                opt.textContent = getModelDisplayName(defaultModel) || defaultModel;
                opt.dataset.provider = defaultProvider;
                menuModelSelect.appendChild(opt);
            }
            if (elements.modelSelect && !Array.from(elements.modelSelect.options).some(o => o.value === defaultModel)) {
                const opt = document.createElement('option');
                opt.value = defaultModel;
                opt.textContent = getModelDisplayName(defaultModel) || defaultModel;
                opt.dataset.provider = defaultProvider;
                elements.modelSelect.appendChild(opt);
            }

            // Set model select and state
            elements.modelSelect.value = defaultModel;
            if (menuModelSelect) menuModelSelect.value = defaultModel;
            state.loadedModel = defaultModel;
            state.loadedProvider = defaultProvider;

            // Set collection select
            const menuCollectionSelect = document.getElementById('menu-collection-select');
            if (menuCollectionSelect) menuCollectionSelect.value = defaults.collection;

            // Load the collection using the standard handler (shows its own loading modal)
            try {
                await loadCollectionHandler(defaults.collection);
                // Update model display button to show loaded model
                const displayName = getModelDisplayName(defaultModel) || defaultModel;
                updateCurrentModelDisplay(defaultModel, displayName, defaultProvider, true);
            } catch (e) {
                console.error('[Init] Failed to load CLI default collection:', e);
                addSystemMessage(`Failed to load default collection: ${defaults.collection}`);
            }
        } else {
            // No CLI defaults - just load default model
            updateMenuLoadButton();
            updateHeaderStatus();

            // Update connecting overlay to show model loading status
            const overlay = document.getElementById('connecting-overlay');
            const text = overlay?.querySelector('.connecting-text');

            // Load the default model (small) into VRAM
            const defaultModel = defaults.model || 'small';
            // Use container config model_type or dropdown's data-provider as source of truth
            const defaultModelOption = elements.modelSelect?.selectedOptions[0];
            const optProvider = defaults.model_type || defaultModelOption?.dataset?.provider;
            const isApiTier = optProvider === 'anthropic' || ['haiku', 'sonnet', 'opus'].includes(defaultModel);
            const isGroq = optProvider === 'groq';
            const isOpenRouter = optProvider === 'openrouter' || ['r1', 'deepseek'].includes(defaultModel) || (!isGroq && defaultModel.includes('/'));

            // Determine provider and loading text
            let provider = 'local';
            let loadingText = 'Loading model';
            if (isApiTier) {
                provider = 'anthropic';
                loadingText = 'Connecting to Claude API';
            } else if (isGroq) {
                provider = 'groq';
                loadingText = 'Connecting to Groq';
            } else if (isOpenRouter) {
                provider = 'openrouter';
                loadingText = 'Connecting to OpenRouter';
            }

            if (text) {
                text.textContent = loadingText;
            }

            try {
                const loadResult = await api.loadModel(defaultModel);

                // Use provider from server response (server is source of truth)
                if (loadResult.provider && loadResult.provider !== 'local') {
                    provider = loadResult.provider;
                }

                // Set the model as loaded
                state.loadedModel = defaultModel;
                state.loadedProvider = provider || 'local';

                // Ensure model exists in dropdowns (cloud models may not be in tiers)
                const menuModelSelect = document.getElementById('menu-model-select');
                if (menuModelSelect && !Array.from(menuModelSelect.options).some(o => o.value === defaultModel)) {
                    const opt = document.createElement('option');
                    opt.value = defaultModel;
                    opt.textContent = loadResult.model || defaultModel;
                    opt.dataset.tier = provider.charAt(0).toUpperCase() + provider.slice(1);
                    opt.dataset.model = loadResult.model || defaultModel;
                    opt.dataset.provider = provider;
                    menuModelSelect.appendChild(opt);
                }
                elements.modelSelect.value = defaultModel;
                if (menuModelSelect) menuModelSelect.value = defaultModel;

                // Update header and load button to show model indicator
                updateMenuLoadButton();
                updateHeaderStatus();

                // Update current model display - use getModelDisplayName for friendly names
                const displayName = getModelDisplayName(defaultModel) || loadResult.model || defaultModel;
                updateCurrentModelDisplay(defaultModel, displayName, provider, true);

                hideConnectingOverlay();
                let message;
                if (provider === 'anthropic' || isApiTier) {
                    message = 'Ready. Connected to Claude API. Open menu to select an agent or start chatting.';
                } else if (provider === 'groq') {
                    message = 'Ready. Connected to Groq. Open menu to select an agent or start chatting.';
                } else if (provider === 'openrouter' || isOpenRouter) {
                    message = 'Ready. Connected to OpenRouter. Open menu to select an agent or start chatting.';
                } else {
                    message = 'Ready. Model loaded. Open menu to select an agent or start chatting.';
                }
                addSystemMessage(message);
            } catch (e) {
                console.error('[Init] Failed to load default model:', e);
                hideConnectingOverlay();
                addSystemMessage('Ready. Open menu to select an agent. Type /help for commands.');
            }
        }
    }

    // Set up event listeners (must come before tool toggles — creates plus menu DOM)
    setupEventListeners();

    // Populate tool toggles now that plus menu DOM exists
    if (state.agentSettings) {
        window.populateToolToggles?.(state.agentSettings.tools || [], state.agentSettings);
    }
}

/**
 * Restore a saved session (agent + conversation)
 */
async function restoreSession(savedSession) {
    const { loadedAgent, modelTier, loadedProvider: savedProvider, sessionId, narratorMode, narratorState } = savedSession;

    try {
        // Quick health check - is the agent already loaded on the server?
        const health = await api.checkHealth(loadedAgent);

        if (health.agent_loaded) {
            // Agent is already loaded - fast restore (no loading overlay)
            state.loadedAgent = loadedAgent;
            state.currentAgent = loadedAgent;
            state.loadedCollection = null;
            state.loadedCollectionTitle = null;
            state.loadedCollectionDescription = null;

            // Restore model state (don't load, model warms on first message)
            if (modelTier) {
                state.loadedModel = modelTier;
                state.loadedProvider = savedProvider || null;
            }

            // Fetch agent settings (needed for tool toggles)
            try {
                const settingsRes = await fetch(`/agents/${encodeURIComponent(loadedAgent)}/settings`, {
                    headers: api.getHeaders(false)
                });
                if (settingsRes.ok) {
                    const data = await settingsRes.json();
                    if (data?.settings) {
                        state.agentSettings = data.settings;
                        state.agentKbCount = data.kb_count || 0;
                    }
                }
            } catch (e) { /* non-critical */ }

            // Restore narrator mode from saved state
            if (narratorMode && narratorState) {
                state.narratorMode = true;
                state.narratorPaused = savedSession.narratorPaused || false;
                state.narratorState = narratorState;
            }

            updateModeIndicators(loadedAgent, null, state.narratorMode, state.narratorState?.chunk_id || 1);
            updateMenuLoadButton();
            updateHeaderStatus();
            hideCollectionHeader();
            window.updateNarratorMenuSection?.();

            // Update voice button icon for narrator mode
            import('./voice.js').then(({ updateVoiceBtnIcon }) => {
                const effectiveNarrator = state.narratorMode && !state.narratorPaused;
                updateVoiceBtnIcon(effectiveNarrator, !!state.agentSettings?.narrator_verbatim);
            }).catch(() => {});

            // Update model display
            if (modelTier) {
                const isApiTier = modelTier === 'haiku' || modelTier === 'sonnet';
                const menuModelSelect = document.getElementById('menu-model-select');
                const selectedOption = menuModelSelect?.selectedOptions?.[0];
                const displayName = selectedOption?.dataset?.model || modelTier;
                const provider = isApiTier ? 'anthropic' : 'local';
                updateCurrentModelDisplay(modelTier, displayName, provider, true);
            }

            // Restore conversation if we have a session ID
            if (sessionId) {
                await restoreConversation(sessionId, loadedAgent);
            }

            hideConnectingOverlay();
            addSystemMessage(`Session restored. ${loadedAgent} ready.`);

            // Re-apply container restrictions after session restore (ensures friendly model name)
            if (isContainerMode()) {
                applyContainerModeRestrictions();
            }

        } else {
            // Agent not loaded - need full load with progress overlay
            await fullAgentRestore(savedSession);
        }

    } catch (e) {
        console.error('[Restore] Health check failed:', e);
        // Server might be down - show error
        showConnectionError();
    }
}

/**
 * Full agent restore with loading overlay (when agent not cached)
 */
async function fullAgentRestore(savedSession) {
    const { loadedAgent, modelTier, loadedProvider: savedProvider, sessionId, narratorMode, narratorState } = savedSession;

    const loadingOverlay = document.getElementById('loading-overlay');
    const loadingTitle = document.getElementById('loading-title');
    const progressFill = document.getElementById('progress-fill');
    const progressText = document.getElementById('progress-text');

    if (loadingOverlay) {
        loadingTitle.textContent = `Restoring ${loadedAgent}`;
        progressFill.style.width = '0%';
        progressText.textContent = 'Loading agent...';
        loadingOverlay.classList.add('active');
    }

    try {
        // Load the agent with the saved model
        const finalData = await api.loadAgent(loadedAgent, (loaded, total, status) => {
            const percent = Math.round((loaded / total) * 100);
            if (progressFill) progressFill.style.width = `${percent}%`;
            if (progressText) progressText.textContent = status || `${loaded} / ${total} KBs`;
        }, modelTier);

        if (finalData) {
            state.loadedAgent = loadedAgent;
            state.currentAgent = loadedAgent;
            state.loadedCollection = null;
            state.loadedCollectionTitle = null;
            state.loadedCollectionDescription = null;
            state.agentSettings = finalData.settings;
            state.agentKbCount = finalData.kb_count || 0;

            // Set loaded model state (model was loaded by api.loadAgent)
            if (modelTier) {
                state.loadedModel = modelTier;
                state.loadedProvider = savedProvider || null;
            }

            // Check if agent has Tier 1 tools (requires LLM tool calling)
            const settings = finalData.settings || {};
            const hasTier1Tools = (settings.tool_python || settings.tool_shell) &&
                                  settings.tool_mode === 'llm_assisted';
            setToolingRequired(hasTier1Tools);

            // Restore narrator mode if it was active
            if (narratorMode && narratorState) {
                state.narratorMode = true;
                state.narratorPaused = savedSession.narratorPaused || false;
                state.narratorState = narratorState;
            } else if (finalData.settings?.narrator_mode) {
                state.narratorMode = true;
                // Fetch server-side narrator progress
                let chunkId = 1;
                let book = finalData.settings.narrator_starting_book || null;
                let kbPath = null;
                try {
                    const progress = await api.fetchNarratorProgress(loadedAgent);
                    if (progress?.current_position) {
                        chunkId = progress.current_position.chunk_id || 1;
                        kbPath = progress.current_position.kb || null;
                    }
                    if (progress?.current_book) {
                        book = progress.current_book;
                    }
                } catch (e) {
                    console.error('[Restore] Failed to fetch narrator progress:', e);
                }
                state.narratorState = {
                    book: book,
                    kb_path: kbPath,
                    chunk_id: chunkId,
                    chunk_batch: state.narratorState.chunk_batch || 1,
                    narration_length: state.narratorState.narration_length ?? 1
                };
            }

            // Populate tool toggles from restored settings
            window.populateToolToggles?.(finalData.settings?.tools || [], finalData.settings);

            updateModeIndicators(loadedAgent, null, state.narratorMode, state.narratorState.chunk_id);
            updateMenuLoadButton();
            updateHeaderStatus();
            hideCollectionHeader();
            window.updateNarratorMenuSection?.();

            // Update voice button icon for narrator mode
            import('./voice.js').then(({ updateVoiceBtnIcon }) => {
                const effectiveNarrator = state.narratorMode && !state.narratorPaused;
                updateVoiceBtnIcon(effectiveNarrator, !!state.agentSettings?.narrator_verbatim);
            }).catch(() => {});

            // Update model display
            if (modelTier) {
                const isApiTier = modelTier === 'haiku' || modelTier === 'sonnet';
                const menuModelSelect = document.getElementById('menu-model-select');
                const selectedOption = menuModelSelect?.selectedOptions?.[0];
                const displayName = selectedOption?.dataset?.model || modelTier;
                const provider = isApiTier ? 'anthropic' : 'local';
                updateCurrentModelDisplay(modelTier, displayName, provider, true);
            }
        }

        // Restore conversation if we have a session ID
        if (sessionId) {
            if (progressText) progressText.textContent = 'Loading conversation...';
            await restoreConversation(sessionId, loadedAgent);
        }

        if (loadingOverlay) loadingOverlay.classList.remove('active');
        hideConnectingOverlay();

        // Show restored message
        if (sessionId) {
            addSystemMessage(`Session restored. ${loadedAgent} ready.`);
        } else {
            addSystemMessage(`${loadedAgent} loaded. Ready to chat.`);
        }

        // Re-apply container restrictions after session restore (ensures friendly model name)
        if (isContainerMode()) {
            applyContainerModeRestrictions();
        }

    } catch (e) {
        console.error('[Restore] Failed to restore session:', e);
        if (loadingOverlay) loadingOverlay.classList.remove('active');
        hideConnectingOverlay();
        addSystemMessage('Ready. Open menu to select an agent. Type /help for commands.');
    }
}

/**
 * Restore conversation history from session ID
 * @param {string} sessionId - The session ID to restore
 * @param {string} assistantName - Optional name to use for assistant message headers
 */
async function restoreConversation(sessionId, assistantName = null) {
    try {
        const res = await fetch(`${API_BASE}/sessions/${sessionId}`, { headers: api.getHeaders(false) });
        if (res.status === 404) {
            // Saved session no longer exists (deleted, wrong scope, scrubbed).
            // Drop the stale id from sessionStorage so we don't retry this 404
            // on every page load — start fresh and let the user's next message
            // create a new conversation via the normal chat flow.
            console.warn('[Restore] Saved session', sessionId, 'is gone; clearing stale state');
            state.sessionId = null;
            saveSessionStateWithModel(state.loadedModel);
            return;
        }
        if (res.ok) {
            const history = await res.json();
            if (history && history.messages && history.messages.length > 0) {
                state.sessionId = sessionId;
                clearChat();

                let lastUserMsg = null;
                let lastUserMsgEl = null;
                let lastAssistantEl = null;
                let lastAssistantId = null;
                let lastAssistantIsError = false;

                // Determine assistant name: use provided, or fall back to loaded agent/collection
                let headerName = assistantName || state.loadedAgent || state.loadedCollectionTitle || null;
                // Format hyphenated agent names: "search-agent" → "Search Agent"
                if (headerName && headerName.includes('-')) {
                    headerName = headerName.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                }

                // Model name for message footers (resolved by server to match live chat format)
                const footerModelName = history.display_model || null;

                // Render all messages (including citations for assistant messages)
                // NOTE: this mirrors loadConversation() in conversations.js —
                // the two renderers drifted (dictations lost their UI on
                // refresh but not on conversation reload). Any new message
                // type handled there must be handled here too.
                for (const msg of history.messages) {
                    // Skip system prompt trigger messages (internal markers)
                    if (msg.role === 'user' && msg.content.startsWith('[SYSTEM:')) {
                        continue;
                    }
                    // Skip narrator "continue" messages (blank/continue triggers)
                    if (msg.role === 'user' && state.narratorMode) {
                        const trimmed = msg.content.trim().toLowerCase();
                        if (!trimmed || trimmed === 'continue') {
                            continue;
                        }
                    }
                    // Fact-ingest chips: role='system' with is_fact_ingest
                    // metadata — dedicated chip component, no bubble.
                    if (msg.role === 'system' && msg.metadata && msg.metadata.is_fact_ingest) {
                        renderFactIngestChip({
                            chip_message_id: msg.id,
                            filename: msg.metadata.filename || 'document',
                            fact_count: msg.metadata.fact_count || 0,
                            facts: msg.metadata.facts || [],
                            source_marker: msg.metadata.source_marker,
                        }, { sessionId, noScroll: true });
                        continue;
                    }
                    // Recording ingests: same idea, richer card — it carries
                    // the recording's parts so they can be opened from chat.
                    if (msg.role === 'system' && msg.metadata && msg.metadata.is_recording_ingest) {
                        renderRecordingIngestCard({
                            ...msg.metadata,
                            chip_message_id: msg.id,
                        }, { sessionId, noScroll: true });
                        continue;
                    }
                    // Note-synthesis markers: internal bookkeeping, never
                    // displayed (renders as a raw-markdown block otherwise).
                    if (msg.role === 'system' && msg.metadata && msg.metadata.is_note_synthesis) {
                        continue;
                    }
                    // Compaction markers: the summary that replaced the turns above
                    // it in the model's view, shown as a divider that opens to it.
                    if (msg.role === 'system' && msg.metadata && msg.metadata.is_compaction) {
                        renderCompactionDivider(msg);
                        continue;
                    }

                    const options = {
                        render: true,
                        noScroll: true,
                        citations: msg.citations || null,
                        attachments: msg.attachments || null,
                        messageId: msg.id,  // Include message ID for delete functionality
                        toolsUsed: msg.tools_used || null,  // Tools used for this response
                        stepLog: msg.step_log || null  // Step accordion data
                    };
                    // Dictation messages: render with dictation styling
                    // (assistant-like with badge + Edit footer)
                    if (msg.metadata && msg.metadata.is_dictation) {
                        options.isDictation = true;
                    }
                    // Ingested-document messages: user-side bubble with
                    // filename chip + click-to-expand text body.
                    if (msg.metadata && msg.metadata.is_document && msg.role === 'user') {
                        options.isDocument = true;
                        options.filename = msg.metadata.filename || 'document';
                    }
                    // Include narrator data if present (for delete revert functionality)
                    if (msg.narrator_chunk_id) {
                        options.narratorChunkId = msg.narrator_chunk_id;
                        options.narratorBook = msg.narrator_book;
                        options.narratorSection = msg.narrator_section;
                        options.narratorTotalChunks = msg.narrator_total_chunks;
                        options.narratorKbPath = msg.narrator_kb_path;
                        options.narratorChunkCount = msg.narrator_chunk_count || 1;
                    }
                    // Add headers for messages (dictations render their own
                    // badge — no "You" header, matching loadConversation)
                    if (msg.role === 'assistant' && headerName) {
                        options.header = headerName;
                    } else if (msg.role === 'user' && !options.isDictation) {
                        options.header = 'You';
                    }
                    // Separate thinking content from assistant messages
                    let displayContent = msg.content;
                    let thinkingText = null;
                    if (msg.role === 'assistant' && hasThinkingContent(msg.content)) {
                        const { thinking, response } = separateThinkingContent(msg.content);
                        displayContent = response;
                        thinkingText = thinking;
                    }

                    const msgEl = addMessage(msg.role, displayContent, options);

                    // Add collapsed thinking section if present
                    if (thinkingText && msgEl) {
                        addThinkingToMessage(msgEl, thinkingText, true);
                    }

                    // Add model footer to assistant messages (matches live chat format)
                    if (msg.role === 'assistant' && footerModelName && msgEl) {
                        addMetaToMessage(msgEl, `Model: ${footerModelName}`);
                    }

                    // Restore per-message analytics footer
                    if (msg.role === 'assistant' && msg.metadata && msgEl) {
                        addAnalyticsToMessage(msgEl, msg.metadata);
                    }

                    // Track the retry target and the last reply. A failed turn
                    // keeps its error text across a reload but used to lose
                    // Retry / Change Model / Delete, because those were only
                    // ever attached live when the stream broke.
                    if (msg.role === 'user') {
                        lastUserMsg = { message: msg.content, attachments: msg.attachments || [] };
                        lastUserMsgEl = msgEl;
                    }
                    if (msg.role === 'assistant') {
                        lastAssistantEl = msgEl;
                        lastAssistantId = msg.id || null;
                        lastAssistantIsError = isErrorMessageContent(msg.content);
                    }
                }

                restoreRetryAffordances({
                    isError: lastAssistantIsError,
                    lastUserMsg,
                    lastUserMsgEl,
                    lastAssistantEl,
                    lastAssistantId,
                });

                // Scroll to bottom
                const chatContainer = document.getElementById('chat-container');
                if (chatContainer) {
                    chatContainer.scrollTop = chatContainer.scrollHeight;
                }
            }
        }
    } catch (e) {
        console.error('[Restore] Failed to load conversation:', e);
    }
}

/**
 * Restore a saved collection session
 */
async function restoreCollectionSession(savedSession) {
    const { loadedCollection, loadedCollectionTitle, modelTier, loadedProvider: savedProvider, sessionId } = savedSession;

    const loadingOverlay = document.getElementById('loading-overlay');
    const loadingTitle = document.getElementById('loading-title');
    const progressFill = document.getElementById('progress-fill');
    const progressText = document.getElementById('progress-text');

    // Get collection title for display
    const displayTitle = loadedCollectionTitle || loadedCollection;

    if (loadingOverlay) {
        loadingTitle.textContent = `Restoring ${displayTitle}`;
        progressFill.style.width = '0%';
        progressText.textContent = 'Loading collection...';
        loadingOverlay.classList.add('active');
    }

    try {
        // Load the collection (pass model tier to determine local vs API)
        const finalData = await api.loadCollection(loadedCollection, (loaded, total, status) => {
            const percent = Math.round((loaded / total) * 100);
            if (progressFill) progressFill.style.width = `${percent}%`;
            if (progressText) progressText.textContent = status || `${loaded} / ${total} KBs`;
        }, modelTier);

        if (finalData) {
            state.loadedCollection = loadedCollection;
            state.loadedCollectionTitle = finalData.title || loadedCollectionTitle || loadedCollection;
            state.loadedCollectionDescription = finalData.description || null;
            state.currentCollection = loadedCollection;
            state.loadedAgent = null;
            state.currentAgent = '';
            state.narratorMode = false;

            // Set loaded model state (model was loaded by api.loadCollection)
            if (modelTier) {
                state.loadedModel = modelTier;
                state.loadedProvider = savedProvider || null;
            }

            updateModeIndicators(null, state.loadedCollectionTitle, false, null);
            updateMenuLoadButton();
            updateHeaderStatus();
            showCollectionHeader(state.loadedCollectionTitle, state.loadedCollectionDescription);

            // Update model display
            if (modelTier) {
                const isApiTier = modelTier === 'haiku' || modelTier === 'sonnet';
                const menuModelSelect = document.getElementById('menu-model-select');
                const selectedOption = menuModelSelect?.selectedOptions?.[0];
                const displayName = selectedOption?.dataset?.model || modelTier;
                const provider = isApiTier ? 'anthropic' : 'local';
                updateCurrentModelDisplay(modelTier, displayName, provider, true);
            }
        }

        // Restore conversation if we have a session ID
        if (sessionId) {
            if (progressText) progressText.textContent = 'Loading conversation...';
            await restoreConversation(sessionId, state.loadedCollectionTitle);
        }

        if (loadingOverlay) loadingOverlay.classList.remove('active');
        hideConnectingOverlay();

        // Re-apply container restrictions after session restore (ensures friendly model name)
        if (isContainerMode()) {
            applyContainerModeRestrictions();
        }

    } catch (e) {
        console.error('[Restore] Failed to restore collection session:', e);
        if (loadingOverlay) loadingOverlay.classList.remove('active');
        hideConnectingOverlay();
        addSystemMessage('Ready. Open menu to select an agent or collection. Type /help for commands.');
    }
}

/**
 * Tool display metadata — icon SVG, short label, and accent color per tool.
 * Tools not listed here (or hidden ones like web_search, vision, text_cleanup) are skipped.
 */
const TOOL_DISPLAY = {
    rag_search:  { label: 'RAG Search', color: '#3b82f6', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/><circle cx="12" cy="10" r="3"/><path d="m15 13-1.5-1.5"/></svg>' },
    python_exec: { label: 'Python',     color: '#22c55e', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>' },
    shell_exec:  { label: 'Shell',      color: '#f97316', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>' },
    file_ops:    { label: 'Files',      color: '#eab308', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2z"/></svg>' },
    browser_check: { label: 'Browser', color: '#0ea5e9', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>' },
    web_fetch:   { label: 'Web Fetch',  color: '#06b6d4', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>' },
    http_request:{ label: 'HTTP',       color: '#10b981', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>' },
    email_send:  { label: 'Email Send', color: '#f97316', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>' },
    email_fetch: { label: 'Email Fetch', color: '#f97316', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/><circle cx="17.5" cy="17.5" r="3.5"/><line x1="20" y1="20" x2="22.5" y2="22.5"/></svg>' },
    image_generate: { label: 'Image Gen', color: '#e879f9', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>' },
    scholar:         { label: 'Scholar',   color: '#6366f1', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>' },
    agent_db:    { label: 'Database',   color: '#a855f7', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14a9 3 0 0 0 18 0V5"/><path d="M3 12a9 3 0 0 0 18 0"/></svg>' },
    doc_create:  { label: 'Doc Create', color: '#ec4899', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M12 18v-6"/><path d="M9 15h6"/></svg>' },
    calculator:  { label: 'Calc',       color: '#6b7280', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="8" y2="10"/><line x1="12" y1="10" x2="12" y2="10"/><line x1="16" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="8" y2="14"/><line x1="12" y1="14" x2="12" y2="14"/><line x1="16" y1="14" x2="16" y2="14"/></svg>' },
    datetime:    { label: 'DateTime',   color: '#6b7280', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>' },
    web_search:    { label: 'Smart Search', color: '#f59e0b', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.34-4.34"/><path d="m11 8-2 2 2 2"/><path d="m13 8-2 2 2 2"/></svg>' },
    vision:        { label: 'Vision',       color: '#8b5cf6', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>' },
    text_cleanup:  { label: 'Proofread',    color: '#14b8a6', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>' },
    kb_embed:      { label: 'KB Embed',     color: '#8b5cf6', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12"/><path d="M8 11l4 4 4-4"/><rect x="4" y="17" width="16" height="4" rx="1"/></svg>' },
    recall:        { label: 'Recall',        color: '#a78bfa', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><polyline points="3 4 3 10 9 10"/><path d="M12 7v5l3 2"/></svg>' },
    synthesize:    { label: 'Synthesize',    color: '#a78bfa', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5h6"/><path d="M4 12h6"/><path d="M4 19h6"/><path d="M10 5c5 0 4 7 9 7"/><path d="M10 19c5 0 4-7 9-7"/></svg>' },
    brain_query:   { label: 'Brains',        color: '#f472b6', icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="8" ry="3"/><path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5"/><path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6"/></svg>' },
};

// Mapping from agent settings boolean flags to tool names
const SETTINGS_TOOL_FLAGS = {
    tool_vision: 'vision',
    tool_text_cleanup: 'text_cleanup',
    tool_doc_create: 'doc_create',
};

/**
 * Populate tool toggle chips in the plus menu based on agent's enabled tools.
 * Called on agent load/switch.
 */
function populateToolToggles(tools, settings) {
    const container = document.getElementById('plus-menu-tools');
    const divider = document.getElementById('plus-menu-tools-divider');
    if (!container) return;

    // Reset disabled tools on each agent load
    resetDisabledTools();
    container.innerHTML = '';

    // Meta agents get a paged view: one page of chips per subordinate agent,
    // cycled with ‹ › buttons or swipe (all subordinates at once would flood
    // the menu). Falls back to the normal single list on fetch failure.
    if (settings?.is_meta && (settings.subordinate_agents || []).length) {
        renderMetaToolPager(container, divider, tools, settings);
        return;
    }

    // Build combined tool list: tools array + boolean setting flags
    const allTools = [...(tools || [])];
    if (settings) {
        for (const [settingKey, toolName] of Object.entries(SETTINGS_TOOL_FLAGS)) {
            if (settings[settingKey] && !allTools.includes(toolName)) {
                allTools.push(toolName);
            }
        }
    }

    // Filter to displayable tools
    const toolConfigs = settings?.tool_configs || {};
    const displayTools = allTools.filter(t => TOOL_DISPLAY[t]);

    if (displayTools.length === 0) {
        container.style.display = 'none';
        if (divider) divider.style.display = 'none';
        return;
    }

    container.style.display = '';
    if (divider) divider.style.display = '';

    // Agent tools title
    const agentName = state.loadedAgent || state.currentAgent || '';
    if (agentName) {
        const title = document.createElement('div');
        title.className = 'plus-menu-tools-title';
        const pretty = agentName.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        title.textContent = `${pretty}'s Available Tools`;
        container.appendChild(title);
    }

    for (const toolName of displayTools) {
        const meta = TOOL_DISPLAY[toolName];
        const chip = document.createElement('button');
        chip.className = 'tool-chip';
        chip.dataset.tool = toolName;
        chip.style.setProperty('--tool-color', meta.color);
        chip.innerHTML = `${meta.icon}<span>${meta.label}</span>`;
        chip.title = toolName;

        // If default_on is false, start this tool disabled
        const defaultOn = toolConfigs[toolName]?.default_on !== false;
        if (!defaultOn) {
            toggleTool(toolName);  // Add to disabledTools set
            chip.classList.add('disabled');
        }

        chip.addEventListener('click', (e) => {
            e.stopPropagation();
            const enabled = toggleTool(toolName);
            chip.classList.toggle('disabled', !enabled);
        });

        container.appendChild(chip);
    }

    // Add chain chips (from tool_chains settings)
    const chains = settings?.tool_chains || [];
    const chainIcon = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>';
    for (const chain of chains) {
        if (!chain.enabled) continue;
        const chainToolName = `chain_${chain.id.replace(/-/g, '_')}`;
        const chip = document.createElement('button');
        chip.className = 'tool-chip tool-chip-chain';
        chip.dataset.tool = chainToolName;
        chip.style.setProperty('--tool-color', '#f59e0b');
        chip.innerHTML = `${chainIcon}<span>${escapeHtml(chain.name || chain.id)}</span>`;
        chip.title = `Chain: ${chain.description || chain.name || chain.id}`;

        const defaultOn = chain.default_on !== false;
        if (!defaultOn) {
            toggleTool(chainToolName);
            chip.classList.add('disabled');
        }

        chip.addEventListener('click', (e) => {
            e.stopPropagation();
            const enabled = toggleTool(chainToolName);
            chip.classList.toggle('disabled', !enabled);
        });

        container.appendChild(chip);
    }

    // Planning toggle chip (only if agent has use_planning enabled)
    if (settings?.use_planning) {
        const defaultOn = settings.planning_default_on !== false;
        state.planningEnabled = defaultOn;
        const planIcon = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>';
        const planChip = document.createElement('button');
        planChip.className = 'tool-chip tool-chip-planning';
        planChip.dataset.tool = '__planning__';
        planChip.style.setProperty('--tool-color', '#a78bfa');
        planChip.innerHTML = `${planIcon}<span>Planning</span>`;
        planChip.title = 'Toggle planning step before tool execution';

        if (!defaultOn) {
            planChip.classList.add('disabled');
        }

        planChip.addEventListener('click', (e) => {
            e.stopPropagation();
            const enabled = togglePlanning();
            planChip.classList.toggle('disabled', !enabled);
        });

        container.appendChild(planChip);
    }
}

/**
 * Meta agents: paged tool view in the plus menu. Page 0 = the meta agent's
 * own tools (if any), then one page per subordinate showing that agent's
 * chips. Toggling a chip on a subordinate page disables that tool for that
 * subordinate's delegated runs this session (sent as
 * subordinate_disabled_tools with each chat request).
 */
async function renderMetaToolPager(container, divider, metaTools, metaSettings) {
    container.style.display = '';
    if (divider) divider.style.display = '';
    container.innerHTML = '<div class="plus-menu-tools-title">Loading agent tools…</div>';

    let subs = [];
    try {
        const agentName = state.loadedAgent || state.currentAgent || '';
        const res = await fetch(`${API_BASE}/agents/${encodeURIComponent(agentName)}/subordinate-toolsets`, { headers: getHeaders(false) });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        subs = (await res.json()).subordinates || [];
    } catch (e) {
        console.error('Failed to load subordinate toolsets:', e);
        container.innerHTML = '';
        return; // degrade to no tool section rather than a broken pager
    }

    const pretty = (n) => n.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    const metaName = state.loadedAgent || state.currentAgent || '';

    // Build pages: meta agent first (only if it has displayable tools or planning)
    const pages = [];
    const metaDisplayTools = collectDisplayTools(metaTools, metaSettings);
    if (metaDisplayTools.length || metaSettings?.use_planning) {
        pages.push({ kind: 'self', name: metaName, tools: metaDisplayTools, settings: metaSettings });
    }
    for (const sub of subs) {
        const subTools = collectDisplayTools(sub.tools, sub);
        pages.push({ kind: 'sub', name: sub.name, tools: subTools, settings: sub, description: sub.description });
        // Apply per-tool default_on=false as the starting disabled state
        const cfgs = sub.tool_configs || {};
        for (const t of subTools) {
            if (cfgs[t]?.default_on === false && !state.subordinateDisabledTools[sub.name]?.has(t)) {
                toggleSubordinateTool(sub.name, t);
            }
        }
    }
    if (!pages.length) {
        container.style.display = 'none';
        if (divider) divider.style.display = 'none';
        return;
    }

    let current = 0;
    container.innerHTML = `
        <div class="meta-tool-pager-header">
            <button type="button" class="meta-pager-btn" data-dir="-1" title="Previous agent">‹</button>
            <div class="plus-menu-tools-title meta-pager-title"></div>
            <button type="button" class="meta-pager-btn" data-dir="1" title="Next agent">›</button>
        </div>
        <div class="meta-tool-page"></div>
    `;
    const titleEl = container.querySelector('.meta-pager-title');
    const pageEl = container.querySelector('.meta-tool-page');

    // Every agent's chips stay in one stacked grid cell and only the current
    // one shows, so the area is always as tall as the tallest agent. The menu
    // is anchored at its bottom: if this height changed, everything above it,
    // the pager arrows included, would jump on every cycle.
    const buildPanel = (page) => {
        const panel = document.createElement('div');
        panel.className = 'meta-tool-panel';

        if (!page.tools.length && !(page.kind === 'self' && page.settings?.use_planning)) {
            const empty = document.createElement('div');
            empty.className = 'meta-page-empty';
            empty.textContent = 'No toggleable tools';
            panel.appendChild(empty);
        }

        for (const toolName of page.tools) {
            const meta = TOOL_DISPLAY[toolName];
            const chip = document.createElement('button');
            chip.className = 'tool-chip';
            chip.dataset.tool = toolName;
            chip.style.setProperty('--tool-color', meta.color);
            chip.innerHTML = `${meta.icon}<span>${meta.label}</span>`;
            chip.title = toolName;
            chip.addEventListener('click', (e) => {
                e.stopPropagation();
                const enabled = page.kind === 'self'
                    ? toggleTool(toolName)
                    : toggleSubordinateTool(page.name, toolName);
                chip.classList.toggle('disabled', !enabled);
            });
            panel.appendChild(chip);
        }

        // Planning chip only applies to the meta agent's own turn
        if (page.kind === 'self' && page.settings?.use_planning) {
            const planIcon = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>';
            const planChip = document.createElement('button');
            planChip.className = 'tool-chip tool-chip-planning';
            planChip.dataset.tool = '__planning__';
            planChip.style.setProperty('--tool-color', '#a78bfa');
            planChip.innerHTML = `${planIcon}<span>Planning</span>`;
            planChip.addEventListener('click', (e) => {
                e.stopPropagation();
                const enabled = togglePlanning();
                planChip.classList.toggle('disabled', !enabled);
            });
            panel.appendChild(planChip);
        }
        return panel;
    };

    let panels = [];
    const isChipDisabled = (page, tool) => {
        if (tool === '__planning__') return !state.planningEnabled;
        return page.kind === 'self'
            ? state.disabledTools.has(tool)
            : !!state.subordinateDisabledTools[page.name]?.has(tool);
    };

    const renderPage = () => {
        const page = pages[current];
        const who = page.kind === 'self' ? `${pretty(page.name)} (this agent)` : pretty(page.name);
        titleEl.textContent = `${who} · ${current + 1}/${pages.length}`;
        titleEl.title = page.description || '';
        panels.forEach((panel, i) => panel.classList.toggle('active', i === current));
        panels[current].querySelectorAll('.tool-chip').forEach((chip) => {
            chip.classList.toggle('disabled', isChipDisabled(page, chip.dataset.tool));
        });
    };

    // Apply default_on=false for the meta agent's own tools once
    const metaCfgs = metaSettings?.tool_configs || {};
    for (const t of metaDisplayTools) {
        if (metaCfgs[t]?.default_on === false && !state.disabledTools.has(t)) {
            toggleTool(t);
        }
    }
    if (metaSettings?.use_planning) {
        state.planningEnabled = metaSettings.planning_default_on !== false;
    }

    container.querySelectorAll('.meta-pager-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const dir = parseInt(btn.dataset.dir);
            current = (current + dir + pages.length) % pages.length;
            renderPage();
        });
    });

    // Swipe left/right to cycle pages
    let touchStartX = null;
    pageEl.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
    pageEl.addEventListener('touchend', (e) => {
        if (touchStartX === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        touchStartX = null;
        if (Math.abs(dx) > 40) {
            current = (current + (dx < 0 ? 1 : -1) + pages.length) % pages.length;
            renderPage();
        }
    }, { passive: true });

    panels = pages.map(buildPanel);
    pageEl.append(...panels);
    renderPage();
}

/** Combined displayable tool list: tools array + boolean flag tools. */
function collectDisplayTools(tools, settings) {
    const all = [...(tools || [])];
    if (settings) {
        for (const [settingKey, toolName] of Object.entries(SETTINGS_TOOL_FLAGS)) {
            if (settings[settingKey] && !all.includes(toolName)) all.push(toolName);
        }
    }
    return all.filter(t => TOOL_DISPLAY[t]);
}

// Expose globally so chat.js and capsule.js can call on agent load
window.populateToolToggles = populateToolToggles;
window.initPlusMenu = initPlusMenu;

/**
 * Initialize plus menu (web search + attach image + tool toggles)
 */
function initPlusMenu() {
    const plusBtn = document.getElementById('plus-menu-btn');
    const plusMenu = document.getElementById('plus-menu');
    const searchItem = document.getElementById('plus-menu-search');
    const attachItem = document.getElementById('plus-menu-attach');
    const badge = document.getElementById('search-depth-badge');
    const desc = document.getElementById('search-depth-desc');
    const dot = plusBtn?.querySelector('.active-dot');
    if (!plusBtn || !plusMenu) return;

    const depthLabels = { off: 'Off', surface: 'Surface', shallow: 'Shallow', deep: 'Deep' };
    const depthDescs = { off: '', surface: 'Quick snippets', shallow: 'Reads top pages', deep: 'Full research' };

    function toggleMenu(open) {
        const isOpen = open ?? !plusMenu.classList.contains('open');
        plusMenu.classList.toggle('open', isOpen);
        plusBtn.classList.toggle('open', isOpen);
        if (isOpen) window.updateCapsuleMenuVisibility?.();
    }

    // Update badge, description, dot indicator, and icon glow
    function updateSearchUI(depth) {
        if (!badge) return;
        badge.className = `plus-menu-badge depth-${depth}`;
        badge.textContent = depthLabels[depth] || 'Off';
        if (desc) desc.textContent = depthDescs[depth] || '';
        // Active dot on plus button
        if (dot) {
            dot.className = 'active-dot' + (depth !== 'off' ? ` visible ${depth}` : '');
        }
        // Icon glow on search item
        if (searchItem) {
            searchItem.classList.remove('glow-surface', 'glow-shallow', 'glow-deep');
            if (depth !== 'off') searchItem.classList.add(`glow-${depth}`);
        }
        // Narrator verbatim mode disabled state (non-verbatim has full features)
        const narratorLocked = state.narratorMode && state.agentSettings?.narrator_verbatim && !state.narratorPaused;
        if (narratorLocked) {
            searchItem?.classList.add('disabled');
        } else {
            searchItem?.classList.remove('disabled');
        }
    }

    // Image search toggle
    const imageSearchItem = document.getElementById('plus-menu-image-search');
    const imageSearchBadge = document.getElementById('image-search-badge');

    function updateImageSearchUI(enabled) {
        if (!imageSearchBadge) return;
        const effectiveEnabled = enabled && state.webSearchDepth !== 'off';
        imageSearchBadge.className = `plus-menu-badge ${effectiveEnabled ? 'imgsearch-on' : 'imgsearch-off'}`;
        imageSearchBadge.textContent = effectiveEnabled ? 'On' : 'Off';
        const narratorLocked2 = state.narratorMode && state.agentSettings?.narrator_verbatim && !state.narratorPaused;
        if (state.webSearchDepth === 'off' || narratorLocked2) {
            imageSearchItem?.classList.add('disabled');
        } else {
            imageSearchItem?.classList.remove('disabled');
        }
    }

    // Global function for narrator mode toggling, conversation switching etc.
    window.updateWebSearchButtonState = () => {
        updateSearchUI(state.webSearchDepth);
        updateImageSearchUI(state.imageSearchEnabled);
    };

    // Initial state
    updateSearchUI(state.webSearchDepth);
    updateImageSearchUI(state.imageSearchEnabled);

    // Toggle menu
    plusBtn.addEventListener('click', () => toggleMenu());

    // Web search: cycle depth (menu stays open)
    searchItem?.addEventListener('click', (e) => {
        e.stopPropagation();
        const narratorLocked3 = state.narratorMode && state.agentSettings?.narrator_verbatim && !state.narratorPaused;
        if (narratorLocked3) return;
        const newDepth = cycleWebSearchDepth();
        updateSearchUI(newDepth);
        updateImageSearchUI(state.imageSearchEnabled);
    });

    // Image search: toggle on/off (menu stays open)
    imageSearchItem?.addEventListener('click', (e) => {
        e.stopPropagation();
        const narratorLocked4 = state.narratorMode && state.agentSettings?.narrator_verbatim && !state.narratorPaused;
        if (state.webSearchDepth === 'off' || narratorLocked4) return;
        const enabled = toggleImageSearch();
        updateImageSearchUI(enabled);
    });

    // Attach image: close menu, trigger file picker
    attachItem?.addEventListener('click', () => {
        toggleMenu(false);
        document.getElementById('image-file-input')?.click();
    });

    // Take picture: close menu, open camera
    const cameraItem = document.getElementById('plus-menu-camera');
    cameraItem?.addEventListener('click', () => {
        toggleMenu(false);
        openCamera();
    });

    // Take Video: same camera modal, MediaRecorder instead of a canvas grab
    const videoItem = document.getElementById('plus-menu-video');
    videoItem?.addEventListener('click', () => {
        toggleMenu(false);
        openVideoCamera();
    });

    // Ingest document: close menu, open ingest modal
    const ingestItem = document.getElementById('plus-menu-ingest');
    ingestItem?.addEventListener('click', () => {
        toggleMenu(false);
        // Lazy-import so the ingest module + its deps aren't loaded until
        // the user actually uses the feature.
        import('./ingest.js').then(({ openIngestModal }) => openIngestModal());
    });

    // Record audio: close menu, open audio manager modal
    const recordItem = document.getElementById('plus-menu-record');
    recordItem?.addEventListener('click', () => {
        toggleMenu(false);
        openAudioManager();
    });

    // Check audio import availability and show/hide record menu item
    (async () => {
        try {
            const res = await fetch(`${API_BASE}/import/audio/status`, { headers: api.getHeaders(false) });
            if (res.ok) {
                const data = await res.json();
                if (data.available) {
                    recordItem.style.display = '';
                }
            }
        } catch (e) { /* ignore */ }
    })();

    // Whenever availability changes (page load, tab focus after a board
    // visit), re-apply playback visibility to the buttons already on
    // screen — they were rendered under the OLD answer.
    // A greyed menu entry is dead, not just pale. The class only changed how
    // it looked, so a dictation entry shown disabled (instead of hidden, which
    // read as a bug) would still have started a recording nothing could hear.
    // Caught once, in the capture phase, for every entry present and future.
    document.addEventListener('click', (e) => {
        if (e.target.closest?.('.plus-menu-item.disabled')) {
            e.stopPropagation();
            e.preventDefault();
        }
    }, true);

    document.addEventListener('voice-availability-changed', () => {
        applyPlaybackVisibility();
        const item = document.getElementById('plus-menu-playback');
        if (item) {
            item.classList.toggle('disabled', state.ttsAvailable === false);
            item.title = state.ttsAvailable === false
                ? 'Read-aloud needs a voice worker or the cloud speech lane. Nothing can speak right now.'
                : '';
        }
    });

    // Playback toggle (TTS play buttons on assistant messages)
    const playbackItem = document.getElementById('plus-menu-playback');
    const playbackDivider = document.getElementById('plus-menu-playback-divider');
    const playbackBadge = document.getElementById('playback-badge');

    function updatePlaybackUI(enabled) {
        if (!playbackBadge) return;
        playbackBadge.className = `plus-menu-badge ${enabled ? 'playback-on' : 'playback-off'}`;
        playbackBadge.textContent = enabled ? 'On' : 'Off';
    }

    // Check voice availability and show/hide playback menu item
    (async () => {
        try {
            const res = await fetch(`${API_BASE}/voice/status`, { headers: api.getHeaders(false) });
            if (res.ok) {
                const data = await res.json();
                state.voiceAvailable = data.stt_available ?? data.available;
                state.ttsAvailable = data.tts_available ?? data.available;
                document.dispatchEvent(new CustomEvent('voice-availability-changed'));
                // Playback is about SPEECH; dictation is about the MIC. They
                // are separate lanes now (cloud speech can be off while
                // transcription runs, and a worker can hold Whisper without
                // Kokoro), so each entry follows its own half.
                const ttsOk = state.ttsAvailable !== false;
                const sttOk = state.voiceAvailable !== false;

                playbackItem.style.display = '';
                playbackDivider.style.display = '';
                updatePlaybackUI(state.playbackEnabled);
                // Present but visibly dead, rather than gone: an option that
                // vanishes reads as a bug, while a greyed one with a reason
                // explains the pool.
                playbackItem.classList.toggle('disabled', !ttsOk);
                playbackItem.title = ttsOk
                    ? ''
                    : 'Read-aloud needs a voice worker or the cloud speech lane. Nothing can speak right now.';
                applyPlaybackVisibility();

                const dict = document.getElementById('plus-menu-dictation');
                const dictExtract = document.getElementById('plus-menu-dictation-extract');
                // Same rule as read-aloud above: present but visibly dead,
                // with the reason, rather than gone.
                for (const item of [dict, dictExtract]) {
                    if (!item) continue;
                    item.classList.toggle('disabled', !sttOk);
                    item.title = sttOk
                        ? ''
                        : (data.stt_reason || 'Dictation needs something that can transcribe. Nothing can hear right now.');
                }
            }
        } catch (_) { /* voice not available */ }
    })();

    playbackItem?.addEventListener('click', (e) => {
        e.stopPropagation();
        const newState = togglePlayback();
        updatePlaybackUI(newState);
        // Toggle play buttons on all existing assistant messages
        applyPlaybackVisibility();
    });

    // ─── Dictation toggle ──────────────────────────────────────────────────
    const dictationItem = document.getElementById('plus-menu-dictation');
    const dictationBadge = document.getElementById('dictation-badge');
    const dictationExtractItem = document.getElementById('plus-menu-dictation-extract');
    const dictationExtractBadge = document.getElementById('dictation-extract-badge');
    // The same setting, reachable from narration too. One truth, two doors:
    // reading a book aloud is where it matters most, because verbatim
    // narration needs no model for the reading itself — extraction was the
    // only thing still pulling one in.
    const narratorExtractItem = document.getElementById('plus-menu-narrator-extract');
    const narratorExtractBadge = document.getElementById('narrator-extract-badge');

    function updateDictationUI(enabled) {
        if (dictationBadge) {
            dictationBadge.className = `plus-menu-badge ${enabled ? 'dictation-on' : 'dictation-off'}`;
            dictationBadge.textContent = enabled ? 'On' : 'Off';
        }
        if (dictationExtractItem) {
            dictationExtractItem.style.display = enabled ? '' : 'none';
        }
        // Swap the voice button icon to reflect dictation mode
        import('./voice.js').then(({ updateVoiceBtnIcon }) => {
            updateVoiceBtnIcon(state.narratorMode, !!state.agentSettings?.narrator_verbatim);
        }).catch(() => {});
        // Sync the send-mode picker (popup active option + send button icon
        // dot). The picker, the plus-menu toggle, and any future entry point
        // all flow through this function so state.dictationMode is the only
        // source of truth.
        updateSendModeUI(enabled);
    }

    // ─── Send-mode picker (long-press on send button) ──────────────────────
    //
    // Long-press the send button to reveal a small vertical popup with
    // "Prompt" and "Dictation" options. Active option is highlighted.
    // Selecting an option swaps state.dictationMode, updates the send
    // button icon, and persists via localStorage (handled by
    // toggleDictationMode in state.js).
    //
    // The same state is exposed through the plus-menu's Dictation toggle,
    // so both stay in sync via updateDictationUI → updateSendModeUI.

    const sendBtn = document.getElementById('send-btn');
    const sendModePopup = document.getElementById('send-mode-popup');

    function updateSendModeUI(enabled) {
        if (sendBtn) {
            sendBtn.classList.toggle('mode-dictation', enabled);
            sendBtn.title = enabled
                ? 'Send as dictation (hold for mode)'
                : 'Send message (hold for mode)';
        }
        if (sendModePopup) {
            sendModePopup.querySelectorAll('.send-mode-option').forEach(opt => {
                const isActive = (opt.dataset.mode === 'dictation') === enabled;
                opt.classList.toggle('active', isActive);
                opt.setAttribute('aria-checked', isActive ? 'true' : 'false');
            });
        }
    }

    function setSendMode(mode) {
        const wantDictation = (mode === 'dictation');
        if (wantDictation !== state.dictationMode) {
            toggleDictationMode();  // flips state + persists
        }
        updateDictationUI(state.dictationMode);
    }

    function openSendModePopup() {
        if (!sendModePopup) return;
        // Sync active state before opening so the user sees the current mode
        updateSendModeUI(state.dictationMode);
        sendModePopup.classList.remove('hidden');
        // Two-frame defer so the browser commits the display change before
        // the transform/opacity transition starts — otherwise the open
        // animation gets skipped on a freshly-displayed element.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => sendModePopup.classList.add('open'));
        });
        // Dismiss on outside click — registered next tick so the press that
        // opened it doesn't immediately close it.
        setTimeout(() => {
            document.addEventListener('pointerdown', _outsideHandler, { capture: true });
            document.addEventListener('keydown', _escHandler);
        }, 0);
    }

    function closeSendModePopup() {
        if (!sendModePopup) return;
        sendModePopup.classList.remove('open');
        // Match the CSS transition duration before hiding so the close
        // animation gets to play.
        setTimeout(() => sendModePopup.classList.add('hidden'), 180);
        document.removeEventListener('pointerdown', _outsideHandler, { capture: true });
        document.removeEventListener('keydown', _escHandler);
    }

    function _outsideHandler(e) {
        if (!sendModePopup) return;
        if (sendModePopup.contains(e.target)) return;
        // Don't close if the user pressed the send button itself again —
        // a regular tap shouldn't dismiss; the long-press logic handles toggle.
        if (sendBtn && sendBtn.contains(e.target)) return;
        closeSendModePopup();
    }

    function _escHandler(e) {
        if (e.key === 'Escape') closeSendModePopup();
    }

    if (sendBtn && sendModePopup) {
        // Long-press detection. Track the press start and trigger after
        // ~450ms if the user is still holding. Cancel if they release or
        // move significantly (so a drag-scroll doesn't trigger it).
        const LONGPRESS_MS = 450;
        const MOVE_TOLERANCE_PX = 8;
        let pressTimer = null;
        let pressStartX = 0;
        let pressStartY = 0;
        let longPressFired = false;

        sendBtn.addEventListener('pointerdown', (e) => {
            // Ignore secondary buttons (let right-click work via contextmenu)
            if (e.button !== 0) return;
            longPressFired = false;
            pressStartX = e.clientX;
            pressStartY = e.clientY;
            pressTimer = setTimeout(() => {
                longPressFired = true;
                pressTimer = null;
                openSendModePopup();
            }, LONGPRESS_MS);
        });

        sendBtn.addEventListener('pointermove', (e) => {
            if (!pressTimer) return;
            const dx = e.clientX - pressStartX;
            const dy = e.clientY - pressStartY;
            if (dx * dx + dy * dy > MOVE_TOLERANCE_PX * MOVE_TOLERANCE_PX) {
                clearTimeout(pressTimer);
                pressTimer = null;
            }
        });

        sendBtn.addEventListener('pointerup', () => {
            if (pressTimer) {
                clearTimeout(pressTimer);
                pressTimer = null;
            }
        });

        sendBtn.addEventListener('pointercancel', () => {
            if (pressTimer) {
                clearTimeout(pressTimer);
                pressTimer = null;
            }
        });

        // If a long-press fired, swallow the subsequent click so the
        // send button doesn't ALSO send a message.
        sendBtn.addEventListener('click', (e) => {
            if (longPressFired) {
                e.preventDefault();
                e.stopImmediatePropagation();
                longPressFired = false;
            }
        }, { capture: true });

        // Right-click as a desktop-friendly alternative to long-press
        sendBtn.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            openSendModePopup();
        });

        // Wire the option buttons
        sendModePopup.querySelectorAll('.send-mode-option').forEach(opt => {
            opt.addEventListener('click', () => {
                const mode = opt.dataset.mode;
                if (mode) setSendMode(mode);
                closeSendModePopup();
            });
        });
    }

    function updateDictationExtractUI(enabled) {
        const cls = `plus-menu-badge ${enabled ? 'dictation-extract-on' : 'dictation-extract-off'}`;
        const text = enabled ? 'On' : 'Off';
        // Both badges, always. Two controls over one setting that disagreed
        // about its value would be worse than having only one.
        for (const b of [dictationExtractBadge, narratorExtractBadge]) {
            if (!b) continue;
            b.className = cls;
            b.textContent = text;
        }
    }

    // Initial state
    updateDictationUI(state.dictationMode);
    updateDictationExtractUI(state.dictationExtractFacts);

    dictationItem?.addEventListener('click', (e) => {
        e.stopPropagation();
        const newState = toggleDictationMode();
        updateDictationUI(newState);
    });

    dictationExtractItem?.addEventListener('click', (e) => {
        e.stopPropagation();
        const newState = toggleDictationExtractFacts();
        updateDictationExtractUI(newState);
    });

    narratorExtractItem?.addEventListener('click', (e) => {
        e.stopPropagation();
        const newState = toggleDictationExtractFacts();
        updateDictationExtractUI(newState);
    });

    // Share Link (capsule) button
    const capsuleItem = document.getElementById('plus-menu-capsule');
    const capsuleDivider = document.getElementById('plus-menu-capsule-divider');
    capsuleItem?.addEventListener('click', () => {
        toggleMenu(false);
        if (state.sessionId) {
            openCapsuleManager(state.sessionId);
        }
    });
    // Show capsule button when not in container/capsule mode
    window.updateCapsuleMenuVisibility = () => {
        if (capsuleItem && capsuleDivider) {
            const show = state.sessionId && !state.capsuleMode;
            capsuleItem.style.display = show ? '' : 'none';
            capsuleDivider.style.display = show ? '' : 'none';
        }
    };

    // ── Narrator section ──────────────────────────────────────────────────
    function updateNarratorMenuSection() {
        const section = document.getElementById('plus-menu-narrator');
        const divider = document.getElementById('plus-menu-narrator-divider');
        const show = state.narratorMode;
        if (section) section.style.display = show ? '' : 'none';
        if (divider) divider.style.display = show ? '' : 'none';
        if (!show) return;

        // Pause/play state
        const pauseIcon = document.getElementById('narrator-pause-icon');
        const playIcon = document.getElementById('narrator-play-icon');
        const pauseBadge = document.getElementById('narrator-pause-badge');
        if (pauseIcon) pauseIcon.style.display = state.narratorPaused ? 'none' : '';
        if (playIcon) playIcon.style.display = state.narratorPaused ? '' : 'none';
        if (pauseBadge) {
            pauseBadge.textContent = state.narratorPaused ? 'Paused' : 'Active';
            pauseBadge.className = `plus-menu-badge ${state.narratorPaused ? 'narrator-paused' : 'narrator-active'}`;
        }

        // Verbatim state
        const verbatimBadge = document.getElementById('narrator-verbatim-badge');
        const isVerbatim = state.agentSettings?.narrator_verbatim || false;
        if (verbatimBadge) {
            verbatimBadge.textContent = isVerbatim ? 'On' : 'Off';
            verbatimBadge.className = `plus-menu-badge ${isVerbatim ? 'verbatim-on' : 'verbatim-off'}`;
        }
    }
    window.updateNarratorMenuSection = updateNarratorMenuSection;

    // Pause/Play narrator
    document.getElementById('plus-menu-narrator-pause')?.addEventListener('click', (e) => {
        e.stopPropagation();
        state.narratorPaused = !state.narratorPaused;
        // Pausing puts a model back in play; resuming takes it out again.
        updateHeaderStatus();
        updateNarratorMenuSection();
        window.updateWebSearchButtonState?.();
        updateModeIndicators(state.loadedAgent, state.loadedCollection,
            state.narratorMode, state.narratorState.chunk_id);
        // Update voice button icon
        import('./voice.js').then(({ updateVoiceBtnIcon }) => {
            const effectiveNarrator = state.narratorMode && !state.narratorPaused;
            updateVoiceBtnIcon(effectiveNarrator, !!state.agentSettings?.narrator_verbatim);
        }).catch(() => {});
        saveSessionStateWithModel(state.loadedModel);
    });

    // Toggle verbatim mode
    document.getElementById('plus-menu-narrator-verbatim')?.addEventListener('click', async (e) => {
        e.stopPropagation();
        const newVal = !state.agentSettings?.narrator_verbatim;
        if (state.agentSettings) state.agentSettings.narrator_verbatim = newVal;
        // Verbatim on means no model is consulted, so the header stops
        // naming one; verbatim off brings it back.
        updateHeaderStatus();
        updateNarratorMenuSection();
        window.updateWebSearchButtonState?.();
        updateModeIndicators(state.loadedAgent, state.loadedCollection,
            state.narratorMode, state.narratorState.chunk_id);
        // Update voice button icon
        try {
            const { updateVoiceBtnIcon } = await import('./voice.js');
            const effectiveNarrator = state.narratorMode && !state.narratorPaused;
            updateVoiceBtnIcon(effectiveNarrator, newVal);
        } catch (_) {}
        // Persist to server
        if (state.loadedAgent) {
            fetch(`/agents/${encodeURIComponent(state.loadedAgent)}/settings`, {
                method: 'PUT',
                headers: api.getHeaders(),
                body: JSON.stringify({ key: 'narrator_verbatim', value: newVal })
            }).catch(err => console.error('Failed to save verbatim setting:', err));
        }
    });

    // Jump to Chunk modal — keep plus menu open so the modal sits above it
    document.getElementById('plus-menu-narrator-jump')?.addEventListener('click', async (e) => {
        e.stopPropagation();
        await openNarratorJumpModal();
    });
    const closeJumpModal = () => {
        const m = document.getElementById('narrator-jump-modal');
        if (!m) return;
        m.classList.remove('visible');
        setTimeout(() => m.classList.add('hidden'), 200);
    };
    document.getElementById('narrator-jump-modal')?.addEventListener('click', (e) => {
        if (e.target.id === 'narrator-jump-modal') closeJumpModal();
    });
    document.querySelector('.narrator-jump-close')?.addEventListener('click', closeJumpModal);

    // Lock screen mode
    document.getElementById('plus-menu-narrator-lock')?.addEventListener('click', (e) => {
        e.stopPropagation();
        const overlay = document.getElementById('narrator-lock-overlay');
        if (!overlay) return;

        // Set lock info
        const titleEl = document.getElementById('narrator-lock-title');
        const chunkEl = document.getElementById('narrator-lock-chunk');
        if (titleEl) titleEl.textContent = 'Narrating...';
        if (chunkEl) {
            const book = state.narratorState?.book || '';
            const chunk = state.narratorState?.chunk_id || '';
            chunkEl.textContent = book ? `${book} - Chunk ${chunk}` : '';
        }

        overlay.classList.add('active');
        // Close plus menu
        document.getElementById('plus-menu-container')?.classList.remove('open');
    });

    // Slide-to-unlock logic
    const lockThumb = document.getElementById('narrator-lock-thumb');
    const lockTrack = document.getElementById('narrator-lock-track');
    const lockOverlay = document.getElementById('narrator-lock-overlay');
    if (lockThumb && lockTrack && lockOverlay) {
        let dragging = false;
        let startX = 0;
        let thumbStart = 4;

        function onDragStart(e) {
            dragging = true;
            lockThumb.classList.add('dragging');
            startX = (e.touches ? e.touches[0].clientX : e.clientX);
            thumbStart = lockThumb.offsetLeft;
            e.preventDefault();
        }
        function onDragMove(e) {
            if (!dragging) return;
            e.preventDefault();
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const dx = clientX - startX;
            const maxLeft = lockTrack.offsetWidth - lockThumb.offsetWidth - 4;
            const newLeft = Math.max(4, Math.min(maxLeft, thumbStart + dx));
            lockThumb.style.left = newLeft + 'px';
        }
        function onDragEnd() {
            if (!dragging) return;
            dragging = false;
            lockThumb.classList.remove('dragging');
            const maxLeft = lockTrack.offsetWidth - lockThumb.offsetWidth - 4;
            const currentLeft = parseInt(lockThumb.style.left || '4');
            if (currentLeft >= maxLeft * 0.8) {
                // Unlock
                lockOverlay.classList.remove('active');
            }
            // Reset thumb position
            lockThumb.style.left = '4px';
        }

        lockThumb.addEventListener('mousedown', onDragStart);
        lockThumb.addEventListener('touchstart', onDragStart, { passive: false });
        document.addEventListener('mousemove', onDragMove);
        document.addEventListener('touchmove', onDragMove, { passive: false });
        document.addEventListener('mouseup', onDragEnd);
        document.addEventListener('touchend', onDragEnd);

        // Block all other touches on the overlay
        lockOverlay.addEventListener('touchstart', (e) => {
            if (e.target !== lockThumb && !lockThumb.contains(e.target)) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, { passive: false });
    }

    // Initial narrator section state
    updateNarratorMenuSection();

    // Close on outside click
    document.addEventListener('click', (e) => {
        if (!plusBtn.contains(e.target) && !plusMenu.contains(e.target)) {
            toggleMenu(false);
        }
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && plusMenu.classList.contains('open')) {
            toggleMenu(false);
        }
        // Also close jump modal on Escape
        const jumpModal = document.getElementById('narrator-jump-modal');
        if (e.key === 'Escape' && jumpModal && !jumpModal.classList.contains('hidden')) {
            jumpModal.classList.remove('visible');
            setTimeout(() => jumpModal.classList.add('hidden'), 200);
        }
    });
}

/**
 * Open the narrator "Jump to Chunk" modal — fetches all chunk IDs from
 * the current KB and renders them as horizontally-scrollable chips.
 */
async function openNarratorJumpModal() {
    const modal = document.getElementById('narrator-jump-modal');
    const scroller = document.getElementById('narrator-jump-scroller');
    if (!modal || !scroller) return;

    scroller.innerHTML = '<div class="narrator-jump-loading">Loading chunks...</div>';
    modal.classList.remove('hidden');
    requestAnimationFrame(() => modal.classList.add('visible'));

    // Determine current KB from narrator state
    const kbPath = state.narratorState?.kb_path;
    if (!kbPath) {
        scroller.innerHTML = '<div class="narrator-jump-loading">No KB loaded.</div>';
        return;
    }

    // KB name is the filename stem of the .db file
    const kbName = kbPath.split('/').pop().replace(/\.db$/, '');
    const currentChunkId = state.narratorState?.chunk_id || 0;

    try {
        // Fetch all chunk IDs (pages of 100 until done)
        const chunkIds = [];
        let page = 1;
        while (true) {
            const res = await fetch(`/kb/${encodeURIComponent(kbName)}/chunks?page=${page}&limit=100`, {
                headers: getHeaders(false),
            });
            if (!res.ok) throw new Error(`Failed to fetch chunks (${res.status})`);
            const data = await res.json();
            for (const c of data.chunks || []) chunkIds.push(c.id);
            if (page >= (data.pages || 1)) break;
            page++;
            if (page > 50) break;  // Safety cap (5000 chunks)
        }

        if (chunkIds.length === 0) {
            scroller.innerHTML = '<div class="narrator-jump-loading">No chunks found.</div>';
            return;
        }

        // Build a map of chunk_id → covering message metadata so we can
        // render variable-width chips for messages that span multiple chunks
        // (e.g., "Very Long" length covers 10 chunks → one wide chip)
        const messageCoverage = new Map();  // start_chunk_id → { count, label }
        document.querySelectorAll('.message.assistant[data-narrator-chunk-id]').forEach(el => {
            const start = parseInt(el.dataset.narratorChunkId);
            const count = parseInt(el.dataset.narratorChunkCount) || 1;
            if (!isNaN(start)) {
                messageCoverage.set(start, { count, end: start + count - 1 });
            }
        });

        // Render chips. Group consecutive chunks that share a message span
        // into a single wide chip; standalone unread chunks get a 1-wide chip.
        scroller.innerHTML = '';
        let i = 0;
        while (i < chunkIds.length) {
            const chunkId = chunkIds[i];
            const chip = document.createElement('button');
            chip.className = 'narrator-jump-chip';
            chip.dataset.chunkId = chunkId;

            // Check if this chunk is the START of a covering message
            const cover = messageCoverage.get(chunkId);
            if (cover) {
                // Find how many sequential chunkIds are in this span
                let spanLen = 1;
                while (spanLen < cover.count && i + spanLen < chunkIds.length) {
                    spanLen++;
                }
                const startNum = i + 1;
                const endNum = i + spanLen;
                chip.textContent = spanLen > 1 ? `${startNum}–${endNum}` : String(startNum);
                chip.title = `Chunks ${startNum} to ${endNum} (read together)`;
                // Width proportional to chunk count
                chip.style.minWidth = `${44 + (spanLen - 1) * 22}px`;
                chip.classList.add('read');
                i += spanLen;
            } else {
                chip.textContent = String(i + 1);
                chip.title = `Chunk ${i + 1}`;
                i += 1;
            }

            // Highlight if this chunk is the current/next position
            if (chunkId === currentChunkId) {
                chip.classList.add('current');
            }

            chip.addEventListener('click', async () => {
                modal.classList.remove('visible');
                setTimeout(() => modal.classList.add('hidden'), 200);
                const { jumpToNarratorChunk } = await import('./voice.js');
                jumpToNarratorChunk(parseInt(chip.dataset.chunkId));
            });
            scroller.appendChild(chip);
        }

        // Scroll the current chip into view
        const currentChip = scroller.querySelector('.narrator-jump-chip.current');
        if (currentChip) {
            requestAnimationFrame(() => {
                currentChip.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'center' });
            });
        }
    } catch (err) {
        console.error('[Narrator Jump] Failed to load chunks:', err);
        scroller.innerHTML = '<div class="narrator-jump-loading">Failed to load chunks.</div>';
    }
}

/**
 * Set up all event listeners
 */
function setupEventListeners() {
    // Initialize plus menu (web search + attach)
    initPlusMenu();

    // Initialize enhanced input handling (paste attachments, etc.)
    initInput();
    initContextMeter();
    initCamera();
    initAudioRecorder();

    // Initialize voice chat (checks server for availability)
    initVoice();

    // Initialize TTS playback player
    initTtsPlayer();

    // Send button doubles as the stop button while a response is in flight.
    // setStreamingState(true) adds `.stopping`; this handler branches on it.
    // Enter intentionally only sends, never stops — stopping requires an
    // explicit click so a stray Enter can't accidentally kill a response.
    elements.sendBtn.addEventListener('click', () => {
        if (elements.sendBtn.classList.contains('stopping')) {
            stopAll();
        } else {
            sendMessage();
        }
    });

    // Enter to send on desktop (Shift+Enter for newline)
    // On touch devices (mobile/tablet), Enter inserts a newline — user must tap send
    elements.messageInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
            if (!isTouchDevice) {
                e.preventDefault();
                sendMessage();
            }
        }
    });

    // Agent selection change (hidden select, synced by menu)
    elements.agentSelect.addEventListener('change', () => {
        // Reset loaded state when agent selection changes
        if (state.loadedAgent !== elements.agentSelect.value) {
            state.loadedAgent = null;
        }
        state.currentAgent = elements.agentSelect.value;
        updateMenuLoadButton();
        updateModeIndicators(state.loadedAgent, state.loadedCollection, state.narratorMode, state.narratorState.chunk_id);
        updateHeaderStatus();

        // Reset narrator mode when switching agents
        if (state.narratorMode) {
            resetNarratorState();
            updateModeIndicators(state.loadedAgent, state.loadedCollection, false, 1);
        }
    });

    // Load and New Chat buttons are now in the hamburger menu (menu.js)

    // Auto-resize textarea
    elements.messageInput.addEventListener('input', () => {
        elements.messageInput.style.height = 'auto';
        elements.messageInput.style.height = Math.min(elements.messageInput.scrollHeight, 150) + 'px';
    });

    // Mobile keyboard handling - scroll input into view
    elements.messageInput.addEventListener('focus', () => {
        setTimeout(() => {
            elements.messageInput.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 300); // Delay to wait for keyboard
    });

    // Handle visual viewport resize (mobile keyboard)
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', () => {
            document.body.style.height = `${window.visualViewport.height}px`;
        });
    }
}

// Start the application
init();
